/* @flow */

import VanillaToasts from 'vanillatoasts';
import { createSelector } from 'reselect';
import { type State, type GetState } from '../preact-app/types';
import {
    type NotificacoesState,
    type NivelNotificacao,
} from './notificacoes-types';
import api, { type Poll } from '../utils/api';
import { type Pedido, type EtapasId, type Etapa } from '../pedidos/types';
import { type Entregadores } from '../entregadores/types';
import { type FormaPagamento } from '../formas-pagamento/types';
import { INTERVALO_POLL_NOTIFICACOES } from '../utils/constants';
import tocarSino from '../notificacoes/tocar-sino';
import getPrimeiroNomeClientePedido from '../pedidos/get-primeiro-nome-cliente-pedido';
import atualizarEtapaPedidosPorPolling from '../pedidos/atualizar-etapa-pedidos-por-polling';
import {
    ETAPA_PENDENTE,
    ETAPA_EXECUCAO,
    ETAPA_ENTREGA,
    ETAPAS_EM_ANDAMENTO,
    NIVEL_ATENCAO,
    NIVEL_URGENTE,
    NIVEL_NORMAL,
} from '../pedidos/constants';
import applyLoading from '../utils/apply-loading';
import getNivelNotificacaoPorData from '../pedidos/get-nivel-notificacao-data';
import getDataMaisAntigaPedidos from '../pedidos/get-data-mais-antiga-pedidos';
import { isEmFuncionamento } from './funcionamento';
import { naoTemPendencias, isAtendimentoPermitido } from './empresa';
import { getRelogio } from './relogio';
import atualizarEtapaPedido from '../pedidos/atualizar-etapa-pedido';

type SetPedidosAction = {
  type: "notificacoes/SET_PEDIDOS",
  pedidos: Pedido[],
};

type AdicionarPedidosAction = {
  type: "notificacoes/ADICIONAR_PEDIDOS",
  pedidos: Pedido[],
};

type EditarPedidoAction = {
  type: "notificacoes/EDITAR_PEDIDO",
  id: number,
  dados: Object,
};

type SetPedidoAction = {
  type: "notificacoes/SET_PEDIDO",
  pedido: Pedido,
};

type SetPollingAction = {
  type: "notificacoes/SET_POLLING",
  polling: Poll[],
};

type ReconhecerAction = {
  type: "notificacoes/RECONHECER",
  ids: number[],
};

type ToggleSomAction = {
  type: "notificacoes/SET_SOM",
  som: boolean,
};

type ToggleSomContinuoAction = {
  type: "notificacoes/SET_SOM_CONTINUO",
  somContinuo: boolean,
};

type SetIntervaloSinoAction = {
  type: "notificacoes/SET_INTERVALO_SINO",
  intervalId: IntervalID,
};

type AlternarAtivacaoAction = {
  type: "notificacoes/ALTERNAR_ATIVACAO",
  ativo: boolean,
};

type AlterarTrocoAction = {
  type: "notificacoes/ALTERAR_TROCO",
  id: number,
  troco: number,
};

type AlterarEtapaAction = {
  type: "notificacoes/ALTERAR_ETAPA",
  id: number,
  etapa: Etapa,
};

type AlterarFormaPagamentoAction = {
  type: "notificacoes/ALTERAR_FORMA_PAGAMENTO",
  id: number,
  formaPagamento: FormaPagamento,
};

type AlterarEntregadorAction = {
  type: "notificacoes/ALTERAR_ENTREGADOR",
  id: number,
  entregador: Entregadores,
};

type Action =
  | ReconhecerAction
  | AdicionarPedidosAction
  | EditarPedidoAction
  | SetPollingAction
  | SetPedidoAction
  | ToggleSomAction
  | ToggleSomContinuoAction
  | SetIntervaloSinoAction
  | AlternarAtivacaoAction
  | SetPedidosAction
  | AlterarTrocoAction
  | AlterarEtapaAction
  | AlterarFormaPagamentoAction
  | AlterarEntregadorAction;

// Action Types

const SET_POLLING = 'notificacoes/SET_POLLING';
const ADICIONAR_PEDIDOS = 'notificacoes/ADICIONAR_PEDIDOS';
const EDITAR_PEDIDO = 'notificacoes/EDITAR_PEDIDO';
const ALTERAR_ETAPA = 'notificacoes/ALTERAR_ETAPA';
const ALTERAR_ENTREGADOR = 'notificacoes/ALTERAR_ENTREGADOR';
const ALTERAR_FORMA_PAGAMENTO = 'notificacoes/ALTERAR_FORMA_PAGAMENTO';
const ALTERAR_TROCO = 'notificacoes/ALTERAR_TROCO';
const RECONHECER = 'notificacoes/RECONHECER';
const SET_PEDIDOS = 'notificacoes/SET_PEDIDOS';
const SET_SOM = 'notificacoes/SET_SOM';
const SET_SOM_CONTINUO = 'notificacoes/SET_SOM_CONTINUO';
const SET_INTERVALO_SINO = 'notificacoes/SET_INTERVALO_SINO';
const ALTERNAR_ATIVACAO = 'notificacoes/ALTERNAR_ATIVACAO';

// Action Creator

const setPolling = (polling: Poll[]): SetPollingAction => ({
    type: SET_POLLING,
    polling,
});

export const editarPedido = (
    id: number,
    dados: Object,
): EditarPedidoAction => ({
    type: EDITAR_PEDIDO,
    id,
    dados,
});

export const alterarEtapa = (id: number, etapa: Etapa): AlterarEtapaAction => ({
    type: ALTERAR_ETAPA,
    id,
    etapa,
});

export const alterarEntregador = (
    id: number,
    entregador: Entregadores,
): AlterarEntregadorAction => ({
    type: ALTERAR_ENTREGADOR,
    id,
    entregador,
});

export const alterarFormaPagamento = (
    id: number,
    formaPagamento: FormaPagamento,
): AlterarFormaPagamentoAction => ({
    type: ALTERAR_FORMA_PAGAMENTO,
    id,
    formaPagamento,
});

export const alterarTroco = (
    id: number,
    troco: number,
): AlterarTrocoAction => ({
    type: ALTERAR_TROCO,
    id,
    troco,
});

const reconhecer = (ids: number[]): ReconhecerAction => ({
    type: RECONHECER,
    ids,
});

const setPedidos = (pedidos: Pedido[]): SetPedidosAction => ({
    type: SET_PEDIDOS,
    pedidos,
});

const alternarAtivacao = (ativo: boolean): AlternarAtivacaoAction => ({
    type: ALTERNAR_ATIVACAO,
    ativo,
});

const adicionarPedidos = (pedidos: Pedido[]): AdicionarPedidosAction => ({
    type: ADICIONAR_PEDIDOS,
    pedidos,
});

const setSom = (som: boolean): ToggleSomAction => ({ type: SET_SOM, som });

const setSomContinuo = (somContinuo: boolean): ToggleSomContinuoAction => ({
    type: SET_SOM_CONTINUO,
    somContinuo,
});

const setIntervaloSino = (intervalId: IntervalID): SetIntervaloSinoAction => ({
    type: SET_INTERVALO_SINO,
    intervalId,
});

const apiGetPedidosLoading = applyLoading(api.getPedidos);

// Selectors

const getNotificacoes = (state: State) => state.notificacoes;

export const getPedidos = (state: State) => getNotificacoes(state).pedidos;

export const getPolling = (state: State) => getNotificacoes(state).polling;

export const hasPedido = (state: State) => getPedidos(state).length > 0;

export const isSomAtivo = (state: State) => getNotificacoes(state).som;

export const isSomContinuoAtivo = (state: State) => getNotificacoes(state).somContinuo;

export const isAtivo = (state: State) => getNotificacoes(state).ativo;

const getIntervalIdSino = (state: State) => getNotificacoes(state).intervalIdSino;

const criarGetPedidosPorEtapas: (EtapasId[]) => (State) => Pedido[] = etapas => (
    createSelector(getPedidos, pedidos => pedidos.filter(({ etapasId }) => etapas
        .includes(etapasId)))
);

const criarGetQuantidade: (Function) => (State) => number = selector => (
    createSelector(selector, valor => valor.length)
);

export const getPedidosEmAndamento = criarGetPedidosPorEtapas(
    ETAPAS_EM_ANDAMENTO,
);
export const getPedidosPendentes = criarGetPedidosPorEtapas([ETAPA_PENDENTE]);
export const getPedidosEmExecucao = criarGetPedidosPorEtapas([ETAPA_EXECUCAO]);
export const getPedidosEmEntrega = criarGetPedidosPorEtapas([ETAPA_ENTREGA]);

export const getQuantidadePedidosEmAndamento = criarGetQuantidade(
    getPedidosEmAndamento,
);
export const getQuantidadePedidosPendente = criarGetQuantidade(
    getPedidosPendentes,
);
export const getQuantidadePedidosEmExecucao = criarGetQuantidade(
    getPedidosEmExecucao,
);
export const getQuantidadePedidosEmEntrega = criarGetQuantidade(
    getPedidosEmEntrega,
);

const getDataMaisAntigaPedidosEmAndamento: (State) => number = createSelector(
    getPedidosEmAndamento,
    getDataMaisAntigaPedidos,
);

export const getNivelNotificacao: (State) => NivelNotificacao = createSelector(
    getRelogio,
    getDataMaisAntigaPedidosEmAndamento,
    (ultimoPolling, dataMaisAntiga) => (ultimoPolling
        ? getNivelNotificacaoPorData(ultimoPolling, dataMaisAntiga)
        : NIVEL_NORMAL),
);

const getIntervaloSino: (State) => 3000 | 500 | 0 = createSelector(
    getNivelNotificacao,
    (nivel: NivelNotificacao) => {
        if (nivel === NIVEL_ATENCAO) {
            return 5000;
        }

        if (nivel === NIVEL_URGENTE) {
            return 4000;
        }

        return 0;
    },
);

const isPollOk = (pedidos: Pedido[]) => (poll: Poll) => {
    const pedido = pedidos.find(({ id }) => id === poll.pedidosId);
    return pedido && pedido.etapasId === poll.etapasId;
};

const getReconhecimento: (State) => number[] = createSelector(
    getPolling,
    getPedidos,
    (polling, pedidos) => polling.filter(isPollOk(pedidos)).map(({ id }) => id),
);

const getPedidosIdPolling: (State) => number[] = createSelector(
    getPolling,
    polling => polling.map(({ pedidosId }) => pedidosId),
);

const getPedidosId: (State) => number[] = createSelector(
    getPedidos,
    pedidos => pedidos.map(({ id }) => id),
);

const getPollingNovosPedidos: (State) => Poll[] = createSelector(
    getPolling,
    getPedidosId,
    (polling, pedidosId) => polling.filter(poll => !pedidosId.includes(poll.pedidosId)),
);

const getPedidosDoPolling: (State) => Pedido[] = createSelector(
    getPedidos,
    getPedidosIdPolling,
    (pedidos, pedidosId) => pedidos.filter(({ id }) => pedidosId.includes(id)),
);

const getPedidosPendenteDoPolling: (State) => Pedido[] = createSelector(
    getPedidosDoPolling,
    pedidos => pedidos.filter(({ etapasId }) => etapasId === ETAPA_PENDENTE),
);

const getTituloNotificacaoNovosPedidos: (State) => string = createSelector(
    getPedidosPendenteDoPolling,
    novosPedidos => (novosPedidos.length === 1
        ? `Novo pedido de ${getPrimeiroNomeClientePedido(novosPedidos[0])}`
        : `${novosPedidos.length} novos pedido`),
);

const temPedidoPendenteNoPolling: (State) => boolean = createSelector(
    getPedidosPendenteDoPolling,
    pedidos => pedidos.length > 0,
);

export const isEstabelecimentoAberto: (State) => boolean = createSelector(
    isEmFuncionamento,
    isAtivo,
    naoTemPendencias,
    (...valores) => valores.every(Boolean),
);

// Thunks

const ativarSom = () => (dispatch: Function, getState: GetState) => {
    dispatch(setSom(true));

    const interval = getIntervaloSino(getState());
    if (!interval) {
        return;
    }

    const intervalId = setInterval(() => {
        tocarSino();
        if (getIntervaloSino(getState()) !== interval) {
            clearInterval(intervalId);
            dispatch(ativarSom());
        }
    }, interval);
    dispatch(setIntervaloSino(intervalId));
};

const desativarSom = () => (dispatch: Function, getState: GetState) => {
    const intervalAnterior = getIntervalIdSino(getState());
    if (intervalAnterior) {
        clearInterval(intervalAnterior);
    }

    dispatch(setSom(false));
};

const ativarSomContinuo = () => (dispatch: Function) => {
    dispatch(setSomContinuo(true));
};

const desativarSomContinuo = () => (dispatch: Function, getState: GetState) => {
    const intervalAnterior = getIntervalIdSino(getState());
    if (intervalAnterior) {
        clearInterval(intervalAnterior);
    }
    dispatch(setSomContinuo(false));
};

const toastsNovosPedidos = () => (dispatch: Function, getState: GetState) => {
    if (temPedidoPendenteNoPolling(getState())) {
        VanillaToasts.create({
            title: getTituloNotificacaoNovosPedidos(getState()),
            type: 'brand',
            timeout: 5000,
        });

        if (isSomAtivo(getState())) {
            tocarSino();
        }
    }
};

export const toggleSom = () => (dispatch: Function, getState: GetState) => {
    if (isSomAtivo(getState())) {
        dispatch(desativarSom());
    } else {
        dispatch(ativarSom());
    }
};

export const toggleSomContinuo = () => (
    dispatch: Function,
    getState: GetState,
) => {
    if (isSomContinuoAtivo(getState())) {
        dispatch(desativarSomContinuo());
        api.editarEmpresa({ somContinuo: false });
    } else {
        dispatch(ativarSomContinuo());
        api.editarEmpresa({ somContinuo: true });
    }
};

export const reconhecerPolling = () => (
    dispatch: Function,
    getState: GetState,
) => {
    const reconhecimento = getReconhecimento(getState());
    if (reconhecimento.length > 0) {
        api
            .reconhecimento(reconhecimento)
            .then(() => dispatch(reconhecer(reconhecimento)));
    }
};

const buscarNovosPedidos = () => (dispatch: Function, getState: GetState) => Promise.all(
    getPollingNovosPedidos(getState())
        .map(poll => api.getPedidosParaAtendimentoPorId(poll.pedidosId)),
)
    .then(res => res.map(({ data }) => data))
    .then(novosPedidos => dispatch(adicionarPedidos(novosPedidos)));

const verificarPedidosPendentesLocalStorage = () => (
    dispatch: Function,
    getState: GetState,
) => {
    const isPedidosPendentes = JSON.parse(localStorage.getItem('pedidos-pendentes'));
    if (isPedidosPendentes === true) {
        const interval = getIntervaloSino(getState());
        if (!interval) {
            return;
        }

        const intervalId = setInterval(() => {
            tocarSino();
            if (getIntervaloSino(getState()) !== interval) {
                clearInterval(intervalId);
                dispatch(ativarSom());
            }
        }, interval);
        dispatch(setIntervaloSino(intervalId));
    }
};

const executarPoll = () => async (dispatch: Function, getState: GetState) => {
    const {
        empresa: { unidadesSelecionadas: unidadesNegocio },
    } = getState();

    return api
        .polling({ unidadesNegocio })
        .then(({ data: polling }) => dispatch(setPolling(polling)))
        .then(() => dispatch(buscarNovosPedidos()))
        .then(() => dispatch(toastsNovosPedidos()))
        .then(() => dispatch(reconhecerPolling()))
        .then(() => dispatch(verificarPedidosPendentesLocalStorage()))
        .catch((error) => {
            console.error('Erro no fluxo do Polling');
            console.error(error);

            VanillaToasts.create({
                title: 'Erro na rotina de pedidos novos',
                text: 'Atualize a página para reiniciar o fluxo',
                type: 'danger',
                timeout: 7000,
            });
        });

    // return api
    //     .polling({ unidadesNegocio })
    //     .then(({ data: polling }) => dispatch(setPolling(polling)))
    //     .then(() => dispatch(buscarNovosPedidos()))
    //     .then(() => dispatch(toastsNovosPedidos()))
    //     .then(() => dispatch(reconhecerPolling()))
    //     .then(() => dispatch(verificarPedidosPendentesLocalStorage()));
};

const talvezExecutarPoll = () => (dispatch: Function, getState: GetState) => {
    if (isEstabelecimentoAberto(getState())) {
        dispatch(executarPoll());
    }
};

const buscarPedidos = (unidadesNegocio: number[]) => (dispatch: Function) => apiGetPedidosLoading({
    etapasId: [ETAPA_PENDENTE, ETAPA_EXECUCAO, ETAPA_ENTREGA],
    unidadesNegocio,
    quantidade: 9999,
    ordenacao: 'pedidos_etapas.entrada ASC',
    embed: 'etapas,produtos,contatos',
}).then(({ data: { data } }) => dispatch(setPedidos(data)));

export const getPedidosPorunidadesSelecionadas = () => (
    dispatch: Function,
    getState: GetState,
) => {
    const {
        empresa: { unidadesSelecionadas },
    } = getState();

    dispatch(buscarPedidos(unidadesSelecionadas)).then(() => {
        dispatch(talvezExecutarPoll());

        if (isSomAtivo(getState())) {
            dispatch(ativarSom());
        }

        setInterval(() => {
            dispatch(talvezExecutarPoll());
        }, INTERVALO_POLL_NOTIFICACOES);
    });
};

export const alternarAtivacaoThunk = () => (
    dispatch: Function,
    getState: GetState,
) => {
    if (isAtivo(getState())) {
        dispatch(alternarAtivacao(false));
        api.editarEmpresa({ ultimoPolling: null });
        return;
    }

    dispatch(alternarAtivacao(true));
    dispatch(talvezExecutarPoll());
};

export const iniciarPolling = () => (
    dispatch: Function,
    getState: GetState,
) => {
    const storage = JSON.parse(localStorage.getItem('preact-app-state'));
    const { empresa } = storage.empresa;

    if (document.referrer.includes('/login')) {
        dispatch(alternarAtivacaoThunk());
    }

    if (!isAtendimentoPermitido(getState())) {
        return;
    }

    dispatch(getPedidosPorunidadesSelecionadas());

    if (empresa.somContinuo === '1') {
        dispatch(setSomContinuo(true));
    }
    if (empresa.somContinuo === '0') {
        dispatch(setSomContinuo(false));
    }
};

const initialState = {
    pedidos: [],
    som: true,
    somContinuo: false,
    intervalIdSino: null,
    intervalIdPoll: null,
    polling: [],
    ativo: false,
};

const notificacoesReducer = (
    state: NotificacoesState = initialState,
    action: Action,
): NotificacoesState => {
    switch (action.type) {
    case SET_PEDIDOS:
        return {
            ...state,
            pedidos: action.pedidos,
        };
    case ADICIONAR_PEDIDOS:
        return {
            ...state,
            pedidos: state.pedidos.concat(action.pedidos),
        };
    case SET_POLLING: {
        return {
            ...state,
            pedidos: atualizarEtapaPedidosPorPolling(state.pedidos, action.polling),
            polling: action.polling,
        };
    }
    case SET_SOM:
        return {
            ...state,
            som: action.som,
        };
    case SET_SOM_CONTINUO:
        return {
            ...state,
            somContinuo: action.somContinuo,
        };
    case SET_INTERVALO_SINO:
        return {
            ...state,
            intervalIdSino: action.intervalId,
        };
    case EDITAR_PEDIDO: {
        const { id, dados } = action;
        return {
            ...state,
            pedidos: state
                .pedidos
                .map(pedido => (pedido.id !== id ? pedido : { ...pedido, ...dados })),
        };
    }
    case ALTERNAR_ATIVACAO:
        return {
            ...state,
            ativo: action.ativo,
        };
    case ALTERAR_TROCO: {
        const { id, troco } = action;
        return {
            ...state,
            pedidos: state
                .pedidos
                .map(pedido => (pedido.id !== id ? pedido : { ...pedido, troco })),
        };
    }
    case ALTERAR_FORMA_PAGAMENTO: {
        const { id, formaPagamento } = action;
        return {
            ...state,
            pedidos: state.pedidos.map(pedido => (pedido.id !== id
                ? pedido
                : {
                    ...pedido,
                    formaPagamento,
                    formasPagamentoId: formaPagamento.id,
                })),
        };
    }
    case ALTERAR_ENTREGADOR: {
        const { id, entregador } = action;
        return {
            ...state,
            pedidos: state.pedidos.map(pedido => (pedido.id !== id
                ? pedido
                : {
                    ...pedido,
                    entregador,
                    entregadoresId: entregador.id,
                })),
        };
    }
    case ALTERAR_ETAPA: {
        const { id, etapa } = action;
        return {
            ...state,
            pedidos: state
                .pedidos
                .map(pedido => (pedido.id !== id ? pedido : atualizarEtapaPedido(pedido, etapa))),
        };
    }
    default:
        return state;
    }
};

export default notificacoesReducer;
